import React, { useState, useEffect } from 'react';
import { Calendar, Users, FileText, Bell, Settings, Search, Filter, Plus, Edit, Trash2, Eye, MessageSquare, Phone, Mail, MapPin, Clock, CheckCircle, AlertCircle, X } from 'lucide-react';
import { getComponentClass } from '../../styles/theme';

interface PatientSummary {
  id: string;
  name: string;
  age: number;
  primaryConditions: string[];
  lastVisit: string;
  riskLevel: 'low' | 'medium' | 'high';
  recentAlerts: number;
  dataPoints: number;
  shareLevel: 'basic' | 'full' | 'custom';
  nextAppointment?: string;
  glucoseTrend?: 'improving' | 'stable' | 'declining';
  medicationAdherence?: number;
  lastGlucoseReading?: number;
  lastWeightReading?: number;
  lastBloodPressure?: string;
}

interface Alert {
  id: string;
  patientId: string;
  patientName: string;
  type: 'high_glucose' | 'missed_medication' | 'weight_change' | 'blood_pressure' | 'appointment_reminder';
  severity: 'low' | 'medium' | 'high';
  message: string;
  timestamp: string;
  status: 'new' | 'acknowledged' | 'resolved';
}

interface Appointment {
  id: string;
  patientId: string;
  patientName: string;
  type: 'consultation' | 'follow_up' | 'emergency' | 'telemedicine';
  date: string;
  time: string;
  duration: number;
  status: 'scheduled' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled';
  notes?: string;
}

export const ProviderDashboard: React.FC = () => {
  const [selectedPatient, setSelectedPatient] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'patients' | 'appointments' | 'alerts' | 'ai-insights' | 'chat' | 'analytics'>('overview');
  const [chatQuery, setChatQuery] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRisk, setFilterRisk] = useState<'all' | 'low' | 'medium' | 'high'>('all');

  const [patients] = useState<PatientSummary[]>([
    {
      id: 'patient-1',
      name: 'John Doe',
      age: 45,
      primaryConditions: ['Type 2 Diabetes', 'Hypertension'],
      lastVisit: '2024-01-10',
      riskLevel: 'medium',
      recentAlerts: 2,
      dataPoints: 1247,
      shareLevel: 'full',
      nextAppointment: '2024-01-25',
      glucoseTrend: 'improving',
      medicationAdherence: 85,
      lastGlucoseReading: 142,
      lastWeightReading: 185,
      lastBloodPressure: '135/85'
    },
    {
      id: 'patient-2',
      name: 'Jane Smith',
      age: 38,
      primaryConditions: ['Asthma', 'Seasonal Allergies'],
      lastVisit: '2024-01-08',
      riskLevel: 'low',
      recentAlerts: 0,
      dataPoints: 892,
      shareLevel: 'basic',
      nextAppointment: '2024-02-01',
      medicationAdherence: 92,
      lastWeightReading: 142
    },
    {
      id: 'patient-3',
      name: 'Robert Johnson',
      age: 62,
      primaryConditions: ['Heart Disease', 'Type 2 Diabetes', 'High Cholesterol'],
      lastVisit: '2024-01-15',
      riskLevel: 'high',
      recentAlerts: 3,
      dataPoints: 2156,
      shareLevel: 'full',
      nextAppointment: '2024-01-20',
      glucoseTrend: 'declining',
      medicationAdherence: 67,
      lastGlucoseReading: 198,
      lastWeightReading: 210,
      lastBloodPressure: '148/92'
    }
  ]);

  const [alerts] = useState<Alert[]>([
    {
      id: 'alert-1',
      patientId: 'patient-1',
      patientName: 'John Doe',
      type: 'high_glucose',
      severity: 'medium',
      message: 'Glucose reading of 198 mg/dL at 2:30 PM',
      timestamp: '2024-01-17T14:30:00Z',
      status: 'new'
    },
    {
      id: 'alert-2',
      patientId: 'patient-3',
      patientName: 'Robert Johnson',
      type: 'missed_medication',
      severity: 'high',
      message: 'Missed Metformin dose for 2 consecutive days',
      timestamp: '2024-01-17T10:15:00Z',
      status: 'acknowledged'
    },
    {
      id: 'alert-3',
      patientId: 'patient-3',
      patientName: 'Robert Johnson',
      type: 'blood_pressure',
      severity: 'high',
      message: 'Blood pressure reading of 148/92 mmHg',
      timestamp: '2024-01-17T09:45:00Z',
      status: 'new'
    }
  ]);

  const [appointments] = useState<Appointment[]>([
    {
      id: 'apt-1',
      patientId: 'patient-1',
      patientName: 'John Doe',
      type: 'follow_up',
      date: '2024-01-25',
      time: '14:00',
      duration: 30,
      status: 'scheduled',
      notes: 'Diabetes management review, bring glucose logs'
    },
    {
      id: 'apt-2',
      patientId: 'patient-3',
      patientName: 'Robert Johnson',
      type: 'consultation',
      date: '2024-01-20',
      time: '10:30',
      duration: 45,
      status: 'confirmed',
      notes: 'Urgent consultation for elevated blood pressure'
    },
    {
      id: 'apt-3',
      patientId: 'patient-2',
      patientName: 'Jane Smith',
      type: 'telemedicine',
      date: '2024-02-01',
      time: '15:00',
      duration: 20,
      status: 'scheduled',
      notes: 'Asthma control assessment'
    }
  ]);

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'low': return 'text-green-600 bg-green-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      case 'high': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'high_glucose': return AlertCircle;
      case 'missed_medication': return AlertCircle;
      case 'weight_change': return AlertCircle;
      case 'blood_pressure': return AlertCircle;
      case 'appointment_reminder': return Calendar;
      default: return AlertCircle;
    }
  };

  const getAlertColor = (severity: string) => {
    switch (severity) {
      case 'low': return 'text-blue-600 bg-blue-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      case 'high': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const filteredPatients = patients.filter(patient => {
    const matchesSearch = patient.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         patient.primaryConditions.some(condition => 
                           condition.toLowerCase().includes(searchQuery.toLowerCase())
                         );
    const matchesRisk = filterRisk === 'all' || patient.riskLevel === filterRisk;
    return matchesSearch && matchesRisk;
  });

  const todayAppointments = appointments.filter(apt => 
    apt.date === new Date().toISOString().split('T')[0]
  );

  const newAlerts = alerts.filter(alert => alert.status === 'new');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center space-x-3 mb-2">
            <img 
              src="/zippy-logo-simple.svg" 
              alt="ZippyHealth Logo"
              className="w-8 h-8"
            />
            <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
              Provider Dashboard
            </h2>
          </div>
          <p className="text-slate-600 mt-1">Manage patients and coordinate care with AI-powered insights</p>
        </div>
        <div className="flex items-center space-x-3">
          <button className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-all transform hover:scale-105">
            <Video className="h-4 w-4" />
            <span>Start Telemedicine</span>
          </button>
          <button className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-all transform hover:scale-105">
            <Users className="h-4 w-4" />
            <span>Add Patient</span>
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', label: 'Overview', icon: Eye },
            { id: 'patients', label: 'Patients', icon: Users },
            { id: 'appointments', label: 'Appointments', icon: Calendar },
            { id: 'alerts', label: 'Alerts', icon: AlertCircle },
            { id: 'ai-insights', label: 'AI Insights', icon: Brain },
            { id: 'chat', label: 'Chat', icon: MessageSquare },
            { id: 'analytics', label: 'Analytics', icon: TrendingUp }
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id as any)}
              className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Icon className="h-4 w-4" />
              <span>{label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[600px]">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Quick Stats */}
            <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Patients</p>
                    <p className="text-2xl font-bold text-gray-900">{patients.length}</p>
                  </div>
                  <Users className="h-8 w-8 text-blue-500" />
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Today's Appointments</p>
                    <p className="text-2xl font-bold text-gray-900">{todayAppointments.length}</p>
                  </div>
                  <Calendar className="h-8 w-8 text-green-500" />
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">New Alerts</p>
                    <p className="text-2xl font-bold text-gray-900">{newAlerts.length}</p>
                  </div>
                  <AlertCircle className="h-8 w-8 text-red-500" />
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">High Risk Patients</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {patients.filter(p => p.riskLevel === 'high').length}
                    </p>
                  </div>
                  <Shield className="h-8 w-8 text-orange-500" />
                </div>
              </div>
            </div>

            {/* Recent Alerts */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Recent Alerts</h3>
                </div>
                <div className="p-6">
                  {alerts.slice(0, 5).map((alert) => {
                    const Icon = getAlertIcon(alert.type);
                    return (
                      <div key={alert.id} className="flex items-center space-x-4 py-3 border-b border-gray-100 last:border-b-0">
                        <div className={`p-2 rounded-full ${getAlertColor(alert.severity)}`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{alert.patientName}</p>
                          <p className="text-sm text-gray-600">{alert.message}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(alert.timestamp).toLocaleString()}
                          </p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getAlertColor(alert.severity)}`}>
                          {alert.severity}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Today's Schedule */}
            <div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Today's Schedule</h3>
                </div>
                <div className="p-6">
                  {todayAppointments.length > 0 ? (
                    todayAppointments.map((apt) => (
                      <div key={apt.id} className="py-3 border-b border-gray-100 last:border-b-0">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{apt.patientName}</p>
                            <p className="text-sm text-gray-600">{apt.time} - {apt.duration}min</p>
                            <p className="text-xs text-gray-500">{apt.type}</p>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            apt.status === 'confirmed' ? 'text-green-600 bg-green-50' :
                            apt.status === 'scheduled' ? 'text-blue-600 bg-blue-50' :
                            'text-gray-600 bg-gray-50'
                          }`}>
                            {apt.status}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">No appointments scheduled for today</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'patients' && (
          <div className="space-y-6">
            {/* Search and Filters */}
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search patients by name or condition..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <select
                value={filterRisk}
                onChange={(e) => setFilterRisk(e.target.value as any)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Risk Levels</option>
                <option value="low">Low Risk</option>
                <option value="medium">Medium Risk</option>
                <option value="high">High Risk</option>
              </select>
            </div>

            {/* Patient List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPatients.map((patient) => (
                <div
                  key={patient.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => setSelectedPatient(patient.id)}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{patient.name}</h3>
                      <p className="text-sm text-gray-600">{patient.age} years old</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(patient.riskLevel)}`}>
                      {patient.riskLevel} risk
                    </span>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Conditions</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {patient.primaryConditions.map((condition, index) => (
                          <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                            {condition}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">Last Visit</p>
                        <p className="font-medium">{new Date(patient.lastVisit).toLocaleDateString()}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Data Points</p>
                        <p className="font-medium">{patient.dataPoints}</p>
                      </div>
                    </div>

                    {patient.glucoseTrend && (
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-600">Glucose Trend:</span>
                        <span className={`text-sm font-medium ${
                          patient.glucoseTrend === 'improving' ? 'text-green-600' :
                          patient.glucoseTrend === 'stable' ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {patient.glucoseTrend}
                        </span>
                      </div>
                    )}

                    {patient.medicationAdherence && (
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-600">Adherence:</span>
                        <span className={`text-sm font-medium ${
                          patient.medicationAdherence >= 90 ? 'text-green-600' :
                          patient.medicationAdherence >= 70 ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {patient.medicationAdherence}%
                        </span>
                      </div>
                    )}

                    <div className="flex items-center space-x-2 pt-2">
                      <button className="flex-1 bg-blue-50 text-blue-700 px-3 py-2 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors">
                        View Details
                      </button>
                      <button className="bg-green-50 text-green-700 px-3 py-2 rounded-lg text-sm font-medium hover:bg-green-100 transition-colors">
                        Message
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'appointments' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Upcoming Appointments</h3>
              </div>
              <div className="p-6">
                {appointments.map((apt) => (
                  <div key={apt.id} className="flex items-center justify-between py-4 border-b border-gray-100 last:border-b-0">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                          <Calendar className="h-6 w-6 text-blue-600" />
                        </div>
                      </div>
                      <div>
                        <h4 className="text-lg font-medium text-gray-900">{apt.patientName}</h4>
                        <p className="text-sm text-gray-600">{apt.type.replace('_', ' ')}</p>
                        <p className="text-sm text-gray-500">
                          {new Date(apt.date).toLocaleDateString()} at {apt.time} ({apt.duration} min)
                        </p>
                        {apt.notes && (
                          <p className="text-sm text-gray-500 mt-1">{apt.notes}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        apt.status === 'confirmed' ? 'text-green-600 bg-green-50' :
                        apt.status === 'scheduled' ? 'text-blue-600 bg-blue-50' :
                        apt.status === 'in_progress' ? 'text-yellow-600 bg-yellow-50' :
                        apt.status === 'completed' ? 'text-gray-600 bg-gray-50' :
                        'text-red-600 bg-red-50'
                      }`}>
                        {apt.status}
                      </span>
                      <button className="bg-blue-50 text-blue-700 px-3 py-1 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors">
                        {apt.type === 'telemedicine' ? 'Join Call' : 'Start Session'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'alerts' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Patient Alerts</h3>
              </div>
              <div className="p-6">
                {alerts.map((alert) => {
                  const Icon = getAlertIcon(alert.type);
                  return (
                    <div key={alert.id} className="flex items-center space-x-4 py-4 border-b border-gray-100 last:border-b-0">
                      <div className={`p-3 rounded-full ${getAlertColor(alert.severity)}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="text-lg font-medium text-gray-900">{alert.patientName}</h4>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getAlertColor(alert.severity)}`}>
                            {alert.severity}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{alert.message}</p>
                        <p className="text-xs text-gray-500 mt-2">
                          {new Date(alert.timestamp).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button className="bg-blue-50 text-blue-700 px-3 py-2 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors">
                          View Patient
                        </button>
                        <button className="bg-green-50 text-green-700 px-3 py-2 rounded-lg text-sm font-medium hover:bg-green-100 transition-colors">
                          Message
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'ai-insights' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">AI-Powered Insights</h3>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-lg">
                    <h4 className="text-lg font-semibold text-blue-900 mb-2">Patient Risk Assessment</h4>
                    <p className="text-blue-800 mb-4">
                      AI analysis shows 3 patients at elevated risk this week. 
                      Robert Johnson requires immediate attention due to declining glucose control.
                    </p>
                    <button className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
                      View Details
                    </button>
                  </div>
                  
                  <div className="bg-gradient-to-r from-green-50 to-green-100 p-6 rounded-lg">
                    <h4 className="text-lg font-semibold text-green-900 mb-2">Treatment Recommendations</h4>
                    <p className="text-green-800 mb-4">
                      Based on recent data, consider adjusting John Doe's medication timing 
                      to improve glucose control during afternoon hours.
                    </p>
                    <button className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors">
                      Review Recommendations
                    </button>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-6 rounded-lg">
                  <h4 className="text-lg font-semibold text-purple-900 mb-2">Population Health Insights</h4>
                  <p className="text-purple-800 mb-4">
                    Your patient population shows 15% improvement in medication adherence 
                    compared to last month. Diabetes management outcomes are trending positive.
                  </p>
                  <button className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors">
                    View Analytics
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'chat' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">AI Assistant Chat</h3>
              </div>
              <div className="p-6">
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <p className="text-sm text-gray-600">
                    Ask me about patient data, treatment recommendations, or clinical guidelines.
                  </p>
                </div>
                <div className="space-y-4">
                  <div className="flex space-x-4">
                    <input
                      type="text"
                      placeholder="Ask about patient data or clinical questions..."
                      value={chatQuery}
                      onChange={(e) => setChatQuery(e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors">
                      Send
                    </button>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      <strong>AI Assistant:</strong> I can help you analyze patient data, 
                      provide treatment recommendations, and answer clinical questions. 
                      What would you like to know about your patients?
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Patient Outcomes</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Glucose Control</span>
                    <span className="text-sm font-medium text-green-600">+12%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Medication Adherence</span>
                    <span className="text-sm font-medium text-green-600">+8%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Blood Pressure</span>
                    <span className="text-sm font-medium text-yellow-600">+3%</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Practice Metrics</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Active Patients</span>
                    <span className="text-sm font-medium text-blue-600">47</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Avg. Response Time</span>
                    <span className="text-sm font-medium text-green-600">2.3h</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Patient Satisfaction</span>
                    <span className="text-sm font-medium text-green-600">4.7/5</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Risk Distribution</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Low Risk</span>
                    <span className="text-sm font-medium text-green-600">65%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Medium Risk</span>
                    <span className="text-sm font-medium text-yellow-600">25%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">High Risk</span>
                    <span className="text-sm font-medium text-red-600">10%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
